// First-run onboarding: signed-in users without an org create one here.
// The Cloud Function does the heavy lifting (transactional writes, the
// admin-cap trigger fires for the founder which is allowed since it's the
// first admin); the page just collects the name and surfaces errors.
//
// On success we don't navigate manually — the user-doc snapshot picks up
// the new orgId, OrgContext updates, RequireOrg now lets them through,
// and they land on /. Trusting the listener avoids double-navigation
// races between the explicit navigate() and the snapshot-driven re-render.

import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Building2, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { createOrg } from '../lib/orgs';
import {
  AuthShell,
  Button,
  FormField,
  Input,
  Row,
  Spinner,
  Stack,
} from '../components/ui';
import { ErrorBanner } from './SigninPage';
import SignOutButton from '../components/auth/SignOutButton';

export default function OnboardingCreateOrgPage() {
  const { profile, loading: authLoading } = useAuth();
  const { orgId } = useOrg();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (authLoading) {
    return (
      <AuthShell>
        <Row gap={2} align="center" justify="center">
          <Spinner /> <span className="ds-text-muted">Loading…</span>
        </Row>
      </AuthShell>
    );
  }

  if (orgId) return <Navigate to="/" replace />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await createOrg(name.trim());
      navigate('/', { replace: true });
    } catch (err) {
      console.error('[onboarding] createOrg failed', err);
      setError(extractMessage(err));
      setBusy(false);
    }
  }

  return (
    <AuthShell>
      <Stack gap={6}>
        <Stack gap={2}>
          <Row gap={2} align="center">
            <Building2 size={20} strokeWidth={1.75} color="var(--color-accent)" />
            <h1 style={{ fontSize: 'var(--fs-2xl)' }}>Create your org</h1>
          </Row>
          <p className="ds-text-muted ds-text-sm">
            Welcome{profile?.name ? `, ${profile.name}` : ''}. Name your organisation to get
            started — you&apos;ll be its first admin.
          </p>
        </Stack>

        {error && <ErrorBanner message={error} />}

        <Stack gap={4} as="form" onSubmit={submit}>
          <FormField label="Org name">
            {(id) => (
              <Input
                id={id}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={100}
                autoFocus
                placeholder="Acme Engineering"
              />
            )}
          </FormField>
          <Button
            type="submit"
            variant="primary"
            block
            loading={busy}
            disabled={!name.trim()}
            iconRight={!busy ? <ArrowRight size={14} strokeWidth={2} /> : undefined}
          >
            Create org
          </Button>
        </Stack>

        <Row justify="center" gap={2} align="center">
          <span className="ds-text-tertiary ds-text-xs">Wrong account?</span>
          <SignOutButton />
        </Row>
      </Stack>
    </AuthShell>
  );
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Could not create the org. Try again.';
}
